import { env } from '@/config/env';

/**
 * Клиент админки.
 *
 * Здесь нет ни одной привилегированной операции: всё уходит в Edge
 * Function `admin`, которая проверяет роль сама (и ещё раз в базе).
 * Клиент только доносит действие и показывает ответ — включая отказ,
 * который у неадминистратора и у несуществующего пользователя выглядит
 * одинаково.
 */
export interface ProviderState {
  active: string;
  /** false — работает заглушка, реальная служба не подключена. */
  real: boolean;
}

export interface AdminOverview {
  metrics: Record<string, number>;
  providers: Record<string, ProviderState>;
}

export interface ManagedSetting {
  key: string;
  label: string;
  min: number;
  max: number;
  hint: string;
}

export interface AdminSettingValue {
  key: string;
  value: number;
  description: string;
}

export interface AdminSettings {
  managed: ManagedSetting[];
  values: AdminSettingValue[];
}

export interface AdminFailure {
  id: string;
  userId: string;
  collectionId: string;
  errorCode: string | null;
  createdAt: string;
}

export class AdminError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'AdminError';
    this.code = code;
  }
}

interface ServerError {
  error?: { code?: string; message?: string };
}

async function call<T>(body: Record<string, unknown>, accessToken: string): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/admin`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AdminError('Нет связи с сервером. Проверьте интернет.', 'NETWORK_ERROR');
  }

  const payload = (await response.json().catch(() => null)) as (T & ServerError) | null;

  if (!payload) {
    throw new AdminError('Сервер ответил неожиданно.', 'UNKNOWN_ERROR');
  }

  if (!response.ok || payload.error) {
    throw new AdminError(
      payload.error?.message ?? 'Не удалось выполнить действие.',
      payload.error?.code ?? 'UNKNOWN_ERROR',
    );
  }

  return payload;
}

export function fetchAdminOverview(accessToken: string): Promise<AdminOverview> {
  return call<AdminOverview>({ action: 'overview' }, accessToken);
}

export function fetchAdminSettings(accessToken: string): Promise<AdminSettings> {
  return call<AdminSettings>({ action: 'settings' }, accessToken);
}

export function saveAdminSetting(
  input: { key: string; value: number },
  accessToken: string,
): Promise<{ values: AdminSettingValue[] }> {
  return call({ action: 'setSetting', key: input.key, value: input.value }, accessToken);
}

export function fetchAdminFailures(accessToken: string): Promise<{ failures: AdminFailure[] }> {
  return call({ action: 'failures' }, accessToken);
}

export function fetchAdminAnalytics(
  accessToken: string,
): Promise<{ events: Record<string, number> }> {
  return call({ action: 'analytics' }, accessToken);
}

export interface AdminCatalog {
  /** Весь каталог, включая скрытые: вернуть коллекцию в витрину нужно отсюда. */
  collections: { slug: string; title: string }[];
  disabled: string[];
  /** Коллекции, о которых уже разослан push «✨ Новая коллекция уже доступна». */
  announced: string[];
}

export function fetchAdminCollections(accessToken: string): Promise<AdminCatalog> {
  return call<AdminCatalog>({ action: 'collections' }, accessToken);
}

export function setCollectionVisibility(
  input: { slug: string; enabled: boolean },
  accessToken: string,
): Promise<{ disabled: string[] }> {
  return call({ action: 'setCollection', slug: input.slug, enabled: input.enabled }, accessToken);
}

export function announceCollection(
  input: { slug: string },
  accessToken: string,
): Promise<{ announced: string[] }> {
  return call({ action: 'announceCollection', slug: input.slug }, accessToken);
}

export interface AdminPack {
  id: string;
  label: string;
  credits: number;
  sort: number;
  active: boolean;
  priceStars: number | null;
  priceRubKopecks: number | null;
  priceVkVoices: number | null;
}

export interface AdminPackInput {
  id: string;
  label: string;
  credits: number;
  sort: number;
  active: boolean;
  priceStars: number | null;
}

export interface AdminPayment {
  id: string;
  userId: string | null;
  packId: string;
  provider: string;
  status: string;
  credits: number;
  amount: number;
  currency: string;
  createdAt: string;
  paidAt: string | null;
}

export interface AdminRevenueRow {
  provider: string;
  packId: string;
  currency: string;
  paidCount: number;
  amount: number;
  credits: number;
  refundedCount: number;
}

export function fetchAdminPacks(accessToken: string): Promise<{ packs: AdminPack[] }> {
  return call({ action: 'packs' }, accessToken);
}

export function saveAdminPack(pack: AdminPackInput, accessToken: string): Promise<{ packs: AdminPack[] }> {
  return call({ action: 'savePack', pack }, accessToken);
}

export function fetchAdminPayments(accessToken: string): Promise<{ payments: AdminPayment[] }> {
  return call({ action: 'payments' }, accessToken);
}

export function refundAdminPayment(paymentId: string, accessToken: string): Promise<{ payments: AdminPayment[] }> {
  return call({ action: 'refundPayment', paymentId }, accessToken);
}

export function fetchAdminRevenue(days: 7 | 30, accessToken: string): Promise<{ days: number; rows: AdminRevenueRow[] }> {
  return call({ action: 'revenue', days }, accessToken);
}

export interface AdminCollectionWish {
  id: string;
  body: string;
  createdAt: string;
}

export function fetchAdminCollectionWishes(accessToken: string): Promise<{ wishes: AdminCollectionWish[] }> {
  return call({ action: 'collectionWishes' }, accessToken);
}
